import React from "react";
import { Helmet } from "react-helmet-async";
import { PUBLIC_DOMAIN, buildHreflangAlternates } from "@/lib/seo";
import { LANGS } from "@/i18n/routeMap";

/**
 * Generic SEO head — drops <title>, <meta description>, OpenGraph, canonical,
 * hreflang alternates and any extra JSON-LD payloads passed via `jsonLd`.
 */
export default function SeoHead({
  title,
  description,
  image,
  lang = "en",
  pageKey,
  slug = "",
  slugMap,
  noindex = false,
  readyLocales = LANGS,
  jsonLd = [],
}) {
  const alternates = buildHreflangAlternates({ key: pageKey, slug, slugMap });
  const canonical = alternates[lang] || PUBLIC_DOMAIN;
  const publishableLocales = new Set((readyLocales || []).filter((lg) => LANGS.includes(lg)));
  const jsonLdItems = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const currentLocaleReady = publishableLocales.has(lang);
  const xDefaultLocale = publishableLocales.has("en") ? "en" : [...publishableLocales][0];
  const ogImage = image && image.startsWith("http") ? image : image ? `${PUBLIC_DOMAIN}${image}` : `${PUBLIC_DOMAIN}/og-default.jpg`;
  // Belt-and-braces: if a bot lands on the temporary Emergent host before our
  // client-side redirect fires, force noindex so the URL never enters Google.
  const onTempHost = typeof window !== "undefined" && /\.emergent\.host$/i.test(window.location.host || "");
  const effectiveNoindex = noindex || !currentLocaleReady || onTempHost;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      {effectiveNoindex && <meta name="robots" content="noindex, nofollow" />}

      <link rel="canonical" href={canonical} />
      {[...publishableLocales].map((lg) => (
        <link key={lg} rel="alternate" hrefLang={lg} href={alternates[lg]} />
      ))}
      {xDefaultLocale && <link rel="alternate" hrefLang="x-default" href={alternates[xDefaultLocale]} />}

      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={
        {en:"en_US", es:"es_ES", ru:"ru_RU", fr:"fr_FR",
         de:"de_DE", it:"it_IT", nl:"nl_NL", pl:"pl_PL"}[lang] || "en_US"
      } />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />

      {jsonLdItems
        .filter(Boolean)
        .map((j, i) => (
          <script key={`ld-${j["@type"] || "default"}-${i}`} type="application/ld+json">
            {JSON.stringify(j.inLanguage ? j : { ...j, inLanguage: lang })}
          </script>
        ))}
    </Helmet>
  );
}
